import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { songService } from "../../services/songService";
import { useAuth } from "../../hooks/useAuth";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { userService } from "../../services/userService";
import Header from "../../components/ui/Header";
import SearchBar from "./components/SearchBar";
import SongListItem from "./components/SongListItem";
import FilterStats from "./components/FilterStats";
import LoadingSkeleton from "./components/LoadingSkeleton";
import EmptyState from "./components/EmptyState";
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import CSVImportModal from '../../components/ui/CSVImportModal';
import DuplicateSongModal from '../../components/ui/DuplicateSongModal';
import DuplicateSongCleanupModal from '../../components/ui/DuplicateSongCleanupModal';
import SongFileImportModal from '../../components/ui/SongFileImportModal';
import SongDuplicateResolutionModal from '../../components/ui/SongDuplicateResolutionModal';

export default function SongDatabaseList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedSort, setSelectedSort] = useState('recently-updated');
  const [showSongs, setShowSongs] = useState(false);
  const [viewMode, setViewMode] = useState('compact');
  const [filterNoRoles, setFilterNoRoles] = useState(false);
  const [filterNoAnalysis, setFilterNoAnalysis] = useState(false);
  const [filterNoMusic, setFilterNoMusic] = useState(false);
  const [filterNoVideos, setFilterNoVideos] = useState(false);
  const [stats, setStats] = useState({});
  const [musicals, setMusicals] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [featuredSongIds, setFeaturedSongIds] = useState(new Set());
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showSongFileImport, setShowSongFileImport] = useState(false);
  const [showDuplicateCleanup, setShowDuplicateCleanup] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showDuplicateResolution, setShowDuplicateResolution] = useState(false);
  const [duplicateResolutionData, setDuplicateResolutionData] = useState(null);

  const getSongs = async () => {
    return await songService?.getSongs();
  };

  const getSongStatistics = async () => {
    return await songService?.getSongStatistics();
  };

  const searchSongs = async (term, options) => {
    return await songService?.searchSongs(term, options);
  };

  const createSong = async (songData) => {
    return await songService?.createSong(songData);
  };

  const updateSong = async (songId, songData) => {
    return await songService?.updateSong(songId, songData);
  };

  const removeDuplicateSongs = async (options) => {
    return await songService?.removeDuplicateSongs(options);
  };

  const isSuperUser = () => {
    return user?.role === 'superuser' || user?.is_admin;
  };

  // Placeholder services
  const MusicalService = {
    createSong: async (data) => ({ data, error: null }),
    getMusicals: async () => ({ data: [], error: null })
  };

  const SongAnalysisService = {
    addToFavorites: async (userId, songId) => ({ error: null }),
    removeFromFavorites: async (userId, songId) => ({ error: null }),
    getUserFavorites: async (userId) => ({ data: [], error: null })
  };

  // ✅ Real-time sync for songs table
  useRealtimeSync('songs', {
    onInsert: (newSong) => {
      console.log('New song added:', newSong);
      // Refetch songs to get complete data with relationships
      fetchSongs();
    },
    onUpdate: (updatedSong) => {
      console.log('Song updated:', updatedSong);
      // Update the song in the list
      setSongs(prevSongs => 
        prevSongs?.map(song => 
          song?.id === updatedSong?.id ? { ...song, ...updatedSong } : song
        )
      );
    },
    onDelete: (deletedSong) => {
      console.log('Song deleted:', deletedSong);
      // Remove the song from the list
      setSongs(prevSongs => 
        prevSongs?.filter(song => song?.id !== deletedSong?.id)
      );
    }
  });

  // Load songs on component mount
  useEffect(() => {
    const initializeData = async () => {
      await loadSongs();
      await loadStatistics();
      setShowSongs(true); // Show songs on initial load
    };
    
    initializeData();
  }, []);

  // Fetch songs from database
  const fetchSongs = async () => {
    try {
      const { data, error } = await songService?.get();
      if (error) {
        console.error('Error loading songs:', error);
        setSongs([]);
      } else {
        setSongs(data || []);
      }
    } catch (error) {
      console.error('Failed to load songs:', error);
      setSongs([]);
    }
  };

  // Load songs from Supabase
  const loadSongs = async () => {
    setLoading(true);
    try {
      const { data, error } = await getSongs();
      if (error) {
        console.error('Error loading songs:', error);
        setSongs([]);
      } else {
        // Deduplicate songs: Keep only one song per title per musical (most recent)
        const deduplicatedSongs = deduplicateSongsByMusical(data || []);
        setSongs(deduplicatedSongs);
        setFeaturedSongIds(new Set(deduplicatedSongs?.filter(s => s?.is_featured)?.map(s => s?.id)));
        // Don't automatically set filteredSongs anymore
        if (showSongs) {
          setFilteredSongs(deduplicatedSongs);
        }
      }
    } catch (error) {
      console.error('Failed to load songs:', error);
      setSongs([]);
      if (showSongs) {
        setFilteredSongs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to deduplicate songs by title and musical_id
  const deduplicateSongsByMusical = (songsArray) => {
    // Create a Map to track unique song-musical combinations
    const uniqueSongsMap = new Map();
    
    songsArray?.forEach(song => {
      // Create unique key: musical_id + normalized title
      const normalizedTitle = song?.title?.toLowerCase()?.trim();
      const uniqueKey = `${song?.musical_id}_${normalizedTitle}`;
      
      // Check if we already have this song for this musical
      const existingSong = uniqueSongsMap?.get(uniqueKey);
      
      if (!existingSong) {
        // First occurrence - add it
        uniqueSongsMap?.set(uniqueKey, song);
      } else {
        // Duplicate found - keep the most recently updated one
        const existingUpdatedAt = new Date(existingSong?.updated_at || 0);
        const currentUpdatedAt = new Date(song?.updated_at || 0);
        
        if (currentUpdatedAt > existingUpdatedAt) {
          // Current song is more recent, replace existing
          uniqueSongsMap?.set(uniqueKey, song);
        }
      }
    });
    
    // Convert Map values back to array
    return Array.from(uniqueSongsMap?.values());
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      const { data, error } = await getSongStatistics();
      if (!error && data) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  // Enhanced search handler with fuzzy logic and Enter key requirement
  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    if (!term?.trim()) {
      if (showSongs) {
        // Apply deduplication when clearing search
        setFilteredSongs(deduplicateSongsByMusical(songs));
      }
      setSearchLoading(false);
      return;
    }

    // Show songs when a search is performed
    setShowSongs(true);
    setSearchLoading(true);
    try {
      const { data, error } = await searchSongs(term?.trim(), {
        genre: selectedGenre !== 'all' ? selectedGenre : undefined,
        include_inactive: false,
        limit: 100
      });
      
      if (error) {
        console.error('Fuzzy search error:', error);
        setFilteredSongs([]);
        setError('Search failed. Please try again.');
      } else {
        // Deduplicate search results
        const deduplicatedResults = deduplicateSongsByMusical(data || []);
        setFilteredSongs(deduplicatedResults);
        setError(null);
      }
    } catch (error) {
      console.error('Fuzzy search failed:', error);
      setFilteredSongs([]);
      setError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle View All Songs button click
  const handleViewAllSongs = () => {
    setShowSongs(true);
    setSearchTerm('');
    setSelectedGenre('all');
    let filtered = applyFilters(songs);
    // Apply deduplication to filtered results as well
    filtered = deduplicateSongsByMusical(filtered);
    setFilteredSongs(filtered);
  };

  // Handle genre filter
  const handleGenreFilter = (genre) => {
    setSelectedGenre(genre);
    
    if (!showSongs) return; // Don't filter if songs aren't being shown
    
    let filtered = searchTerm ? filteredSongs : songs;
    
    if (genre !== 'all') {
      filtered = filtered?.filter(song => song?.musical?.genre === genre);
    }
    
    // Apply additional filters
    filtered = applyFilters(filtered);
    
    // Deduplicate before setting filtered songs
    filtered = deduplicateSongsByMusical(filtered);
    
    setFilteredSongs(applySort(filtered, selectedSort));
  };

  // Handle sort
  const handleSort = (sortType) => {
    setSelectedSort(sortType);
    
    if (!showSongs) return; // Don't sort if songs aren't being shown
    
    setFilteredSongs(applySort(filteredSongs, sortType));
  };

  // Apply sort to songs
  const applySort = (songsToSort, sortType) => {
    if (!songsToSort) return [];
    
    return [...songsToSort]?.sort((a, b) => {
      switch (sortType) {
        case 'recently-updated':
          return new Date(b.updated_at) - new Date(a.updated_at);
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'title-asc':
          return a?.title?.localeCompare(b?.title);
        case 'title-desc':
          return b?.title?.localeCompare(a?.title);
        case 'musical-asc':
          return a?.musical?.title?.localeCompare(b?.musical?.title);
        case 'musical-desc':
          return b?.musical?.title?.localeCompare(a?.musical?.title);
        case 'year-asc':
          return (a?.musical?.year_premiered || 0) - (b?.musical?.year_premiered || 0);
        case 'year-desc':
          return (b?.musical?.year_premiered || 0) - (a?.musical?.year_premiered || 0);
        default:
          return 0;
      }
    });
  };

  // Enhanced filtering with new filters
  const applyFilters = (songsToFilter) => {
    let filtered = [...songsToFilter];

    // Apply no roles filter
    if (filterNoRoles) {
      filtered = filtered?.filter(song => 
        !song?.character_roles_count || song?.character_roles_count === 0
      );
    }

    // Apply no analysis filter
    if (filterNoAnalysis) {
      filtered = filtered?.filter(song => 
        (!song?.overview || Object.keys(song?.overview || {})?.length === 0) &&
        (!song?.level1 || !song?.level1?.trim()) &&
        (!song?.level2 || !song?.level2?.trim()) &&
        (!song?.level3 || song?.level3?.length === 0) &&
        (!song?.level4 || Object.keys(song?.level4 || {})?.length === 0) &&
        (!song?.level5 || Object.keys(song?.level5 || {})?.length === 0)
      );
    }

    // Apply no music filter
    if (filterNoMusic) {
      filtered = filtered?.filter(song => 
        (!song?.midi_file_path || !song?.midi_file_path?.trim()) &&
        (!song?.mp3_file_path || !song?.mp3_file_path?.trim())
      );
    }

    // Apply no videos filter
    if (filterNoVideos) {
      filtered = filtered?.filter(song => 
        !song?.video_count || song?.video_count === 0
      );
    }

    return filtered;
  };

  // Add handlers for new filters
  const handleToggleNoRolesFilter = () => {
    const newValue = !filterNoRoles;
    setFilterNoRoles(newValue);
    
    if (showSongs) {
      let filtered = songs;
      
      // Apply genre filter if active
      if (selectedGenre !== 'all') {
        filtered = filtered?.filter(song => song?.musical?.genre === selectedGenre);
      }
      
      // Apply search if active
      if (searchTerm) {
        // Use current filteredSongs as base for search results
        filtered = filteredSongs;
      }
      
      // Apply filters
      filtered = applyFilters(filtered);
      
      // CRITICAL: Deduplicate before setting filtered songs
      filtered = deduplicateSongsByMusical(filtered);
      
      setFilteredSongs(applySort(filtered, selectedSort));
    }
  };

  const handleToggleNoAnalysisFilter = () => {
    const newValue = !filterNoAnalysis;
    setFilterNoAnalysis(newValue);
    
    if (showSongs) {
      let filtered = songs;
      
      // Apply genre filter if active
      if (selectedGenre !== 'all') {
        filtered = filtered?.filter(song => song?.musical?.genre === selectedGenre);
      }
      
      // Apply search if active
      if (searchTerm) {
        filtered = filteredSongs;
      }
      
      // Apply filters
      filtered = applyFilters(filtered);
      
      // CRITICAL: Deduplicate before setting filtered songs
      filtered = deduplicateSongsByMusical(filtered);
      
      setFilteredSongs(applySort(filtered, selectedSort));
    }
  };

  const handleToggleNoMusicFilter = () => {
    const newValue = !filterNoMusic;
    setFilterNoMusic(newValue);
    
    if (showSongs) {
      let filtered = songs;
      
      // Apply genre filter if active
      if (selectedGenre !== 'all') {
        filtered = filtered?.filter(song => song?.musical?.genre === selectedGenre);
      }
      
      // Apply search if active
      if (searchTerm) {
        filtered = filteredSongs;
      }
      
      // Apply filters
      filtered = applyFilters(filtered);
      
      // CRITICAL: Deduplicate before setting filtered songs
      filtered = deduplicateSongsByMusical(filtered);
      
      setFilteredSongs(applySort(filtered, selectedSort));
    }
  };

  const handleToggleNoVideosFilter = () => {
    const newValue = !filterNoVideos;
    setFilterNoVideos(newValue);
    
    if (showSongs) {
      let filtered = songs;
      
      // Apply genre filter if active
      if (selectedGenre !== 'all') {
        filtered = filtered?.filter(song => song?.musical?.genre === selectedGenre);
      }
      
      // Apply search if active
      if (searchTerm) {
        filtered = filteredSongs;
      }
      
      // Apply filters
      filtered = applyFilters(filtered);
      
      // CRITICAL: Deduplicate before setting filtered songs
      filtered = deduplicateSongsByMusical(filtered);
      
      setFilteredSongs(applySort(filtered, selectedSort));
    }
  };

  const handleToggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'full' ? 'compact' : 'full');
  };

  // Handle CSV import for songs
  const handleCSVImport = async (csvData) => {
    try {
      const importedSongs = [];
      const errors = [];

      for (const row of csvData) {
        try {
          // Find the musical by title
          const musical = musicals?.find(m => 
            m?.title?.toLowerCase()?.trim() === row?.musical_title?.toLowerCase()?.trim()
          );

          if (!musical) {
            errors?.push(`Row "${row?.title}": Musical "${row?.musical_title}" not found`);
            continue;
          }

          // Validate and convert numeric fields
          const songData = {
            title: row?.title?.trim(),
            musical_id: musical?.id,
            act_number: row?.act_number ? parseInt(row?.act_number) || 1 : 1,
            scene_number: row?.scene_number ? parseInt(row?.scene_number) || null : null,
            song_order: row?.song_order ? parseInt(row?.song_order) || null : null,
            tempo: row?.tempo?.trim() || null,
            key_signature: row?.key_signature?.trim() || null,
            time_signature: row?.time_signature?.trim() || '4/4',
            duration_seconds: row?.duration_seconds ? parseInt(row?.duration_seconds) || null : null,
            is_featured: row?.is_featured === 'true' || row?.is_featured === '1' || false,
            lyrics: row?.lyrics?.trim() || null,
            audio_url: row?.audio_url?.trim() || null,
            sheet_music_url: row?.sheet_music_url?.trim() || null
          };

          const { data: newSong, error: createError } = await MusicalService?.createSong(songData);

          if (createError) {
            errors?.push(`Row "${row?.title}": ${createError?.message}`);
          } else {
            importedSongs?.push(newSong);
          }
        } catch (err) {
          errors?.push(`Row "${row?.title}": ${err?.message || 'Unknown error'}`);
        }
      }

      if (errors?.length > 0) {
        return {
          error: `Some imports failed:\n${errors?.slice(0, 3)?.join('\n')}${errors?.length > 3 ? '\n...' : ''}`,
          imported: importedSongs?.length
        };
      }

      // Refresh the songs list
      await loadSongs();
      
      return {
        imported: importedSongs?.length
      };
    } catch (err) {
      return {
        error: `Import failed: ${err?.message || 'Unknown error occurred'}`
      };
    }
  };

  // Handle .pages file import
  const handleSongFileImport = async (parsedSongData) => {
    try {
      setError(null);
      
      // Find the musical by title
      const musical = musicals?.find(m => 
        m?.title?.toLowerCase()?.trim() === parsedSongData?.musicalTitle?.toLowerCase()?.trim()
      );

      if (!musical) {
        // If musical not found, we could create it or show an error
        setError(`Musical "${parsedSongData?.musicalTitle}" not found. Please ensure the musical exists in the database first.`);
        return;
      }

      // Prepare song data for creation
      const songData = {
        title: parsedSongData?.title,
        musical_id: musical?.id,
        act_number: parsedSongData?.actNumber || 1,
        scene_number: parsedSongData?.sceneNumber,
        song_order: parsedSongData?.songOrder,
        tempo: parsedSongData?.tempo,
        key_signature: parsedSongData?.keySignature,
        time_signature: parsedSongData?.timeSignature || '4/4',
        duration_seconds: parsedSongData?.durationSeconds,
        lyrics: parsedSongData?.lyrics,
        overview: parsedSongData?.overview,
        level1: parsedSongData?.level1,
        level2: parsedSongData?.level2,
        level3: parsedSongData?.level3,
        level4: parsedSongData?.level4,
        level5: parsedSongData?.level5,
        phrases: parsedSongData?.phrases,
        created_by: user?.id
      };

      const { data: newSong, error: createError } = await createSong(songData);

      if (createError) {
        setError(`Failed to create song: ${createError}`);
        return;
      }

      // Refresh the songs list
      await loadSongs();
      
      // Show success message (you could add a success state)
      console.log('Song imported successfully:', newSong);
      
    } catch (err) {
      console.error('Error importing song from .pages file:', err);
      setError(`Failed to import song: ${err?.message || 'Unknown error'}`);
    }
  };

  // Handle duplicate resolution
  const handleDuplicatesFound = (duplicateData) => {
    setDuplicateResolutionData(duplicateData);
    setShowDuplicateResolution(true);
  };

  // Handle merging duplicate songs
  const handleMergeDuplicateSongs = async (newData, existingSong) => {
    try {
      setError(null);
      
      // Merge the data - new data takes precedence, but keep existing if new is empty
      const mergedData = {
        overview: newData?.overview && Object.keys(newData?.overview)?.length > 0 ? 
          { ...existingSong?.overview, ...newData?.overview } : existingSong?.overview,
        level1: newData?.level1 && newData?.level1?.trim() ? newData?.level1 : existingSong?.level1,
        level2: newData?.level2 && newData?.level2?.trim() ? newData?.level2 : existingSong?.level2,
        level3: newData?.level3 && newData?.level3?.length > 0 ? newData?.level3 : existingSong?.level3,
        level4: newData?.level4 && Object.keys(newData?.level4)?.length > 0 ? 
          { ...existingSong?.level4, ...newData?.level4 } : existingSong?.level4,
        level5: newData?.level5 && Object.keys(newData?.level5)?.length > 0 ? 
          { ...existingSong?.level5, ...newData?.level5 } : existingSong?.level5,
        phrases: newData?.phrases && newData?.phrases?.length > 0 ? newData?.phrases : existingSong?.phrases,
        // Update basic fields if they're better/more complete
        lyrics: newData?.lyrics && newData?.lyrics?.trim() ? newData?.lyrics : existingSong?.lyrics,
        tempo: newData?.tempo || existingSong?.tempo,
        key_signature: newData?.keySignature || existingSong?.key_signature,
        time_signature: newData?.timeSignature || existingSong?.time_signature,
        duration_seconds: newData?.durationSeconds || existingSong?.duration_seconds,
        updated_by: user?.id
      };

      const { error: updateError } = await updateSong(existingSong?.id, mergedData);

      if (updateError) {
        setError(`Failed to merge song: ${updateError}`);
        return;
      }

      // Refresh the songs list
      await loadSongs();
      
      console.log('Songs merged successfully');
      
    } catch (err) {
      console.error('Error merging songs:', err);
      setError(`Failed to merge songs: ${err?.message || 'Unknown error'}`);
    }
  };

  // Handle creating new song despite duplicates
  const handleCreateNewSongDespiteDuplicates = async (songData) => {
    await handleSongFileImport(songData);
  };

  const handleMergeDuplicates = async (duplicates, originalData) => {
    try {
      // Handle merging duplicate songs
      console.log('Merging duplicates:', duplicates, originalData);
      // Implementation would go here
      await loadSongs();
    } catch (err) {
      console.error('Error merging duplicates:', err);
      setError('Failed to merge duplicates. Please try again.');
    }
  };

  const handleCreateNewSong = async (songData) => {
    try {
      // Handle creating new song
      console.log('Creating new song:', songData);
      // Implementation would go here
      await loadSongs();
    } catch (err) {
      console.error('Error creating new song:', err);
      setError('Failed to create new song. Please try again.');
    }
  };

  const handleToggleFavorite = async (songId) => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    const isCurrentlyFavorited = favorites?.has(songId);
    // Optimistic update
    setFavorites(prev => {
      const next = new Set(prev);
      if (isCurrentlyFavorited) next?.delete(songId);
      else next?.add(songId);
      return next;
    });

    try {
      if (isCurrentlyFavorited) {
        await userService?.removeFavoriteSong(user?.id, songId);
      } else {
        await userService?.addFavoriteSong(user?.id, songId);
      }
    } catch (err) {
      // Revert on error
      setFavorites(prev => {
        const next = new Set(prev);
        if (isCurrentlyFavorited) next?.add(songId);
        else next?.delete(songId);
        return next;
      });
      console.error('Error toggling favorite:', err);
    }
  };

  const handleFeatureToggle = async (song) => {
    if (!isSuperUser?.()) return;
    const songId = song?.id;
    const isCurrentlyFeatured = featuredSongIds?.has(songId);
    // Optimistic update
    setFeaturedSongIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyFeatured) next?.delete(songId);
      else next?.add(songId);
      return next;
    });
    try {
      await updateSong(songId, { is_featured: !isCurrentlyFeatured });
    } catch (err) {
      // Revert on error
      setFeaturedSongIds(prev => {
        const next = new Set(prev);
        if (isCurrentlyFeatured) next?.add(songId);
        else next?.delete(songId);
        return next;
      });
      console.error('Error toggling featured:', err);
    }
  };

  // Load musicals for CSV import reference
  useEffect(() => {
    loadMusicalsForReference();
  }, []);

  const loadMusicalsForReference = async () => {
    try {
      const { data } = await MusicalService?.getMusicals();
      setMusicals(data || []);
    } catch (err) {
      console.error('Error loading musicals for reference:', err);
    }
  };

  // Load songs and favorites from Supabase
  useEffect(() => {
    loadSongs();
    loadStatistics();
    if (user) {
      loadFavorites();
    }
  }, [user]);

  const loadFavorites = async () => {
    try {
      const { favorites: favData, error } = await userService?.getFavoriteSongs(user?.id);
      if (!error && favData) {
        const favoriteIds = Array.isArray(favData) ? favData?.map(fav => fav?.id)?.filter(Boolean) : [];
        setFavorites(new Set(favoriteIds));
      } else {
        setFavorites(new Set());
      }
    } catch (err) {
      console.error('Error loading favorites:', err);
      setFavorites(new Set());
    }
  };

  const handleSongClick = (song) => {
    navigate(`/song-detail-page?id=${song?.id}`);
  };

  const handleAddNewSong = () => {
    navigate('/song-analysis-form');
  };

  // Update handleRefresh to reset new filters
  const handleRefresh = () => {
    setSearchTerm('');
    setSelectedGenre('all');
    setSelectedSort('recently-updated');
    setShowSongs(false);
    setViewMode('compact');
    setFilterNoRoles(false);
    setFilterNoAnalysis(false);
    setFilterNoMusic(false);
    setFilterNoVideos(false);
    setLoading(true);
    setError(null);
    setFilteredSongs([]);
    loadSongs();
    loadStatistics();
    if (user) {
      loadFavorites();
    }
  };

  // Handle duplicate cleanup
  const handleDuplicateCleanup = async (options) => {
    try {
      setError(null);
      
      const { data: result, error: cleanupError } = await removeDuplicateSongs({
        similarityThreshold: options?.similarityThreshold || 0.85,
        dryRun: options?.dryRun !== false, // Default to dry run
        updatedBy: user?.id
      });

      if (cleanupError) {
        setError(`Cleanup failed: ${cleanupError?.message}`);
        return { error: cleanupError?.message };
      }

      // Refresh the songs list if actual removal was performed
      if (!options?.dryRun) {
        await loadSongs();
      }

      let message = options?.dryRun 
        ? `Preview: Would remove ${result?.duplicates_removed || 0} duplicate songs from ${result?.groups_processed || 0} duplicate groups`
        : `Successfully removed ${result?.duplicates_removed || 0} duplicate songs from ${result?.groups_processed || 0} groups, keeping ${result?.songs_kept || 0} most recent versions`;

      return {
        message,
        summary: result
      };

    } catch (err) {
      const errorMsg = `Cleanup operation failed: ${err?.message || 'Unknown error'}`;
      setError(errorMsg);
      return { error: errorMsg };
    }
  };

  // CSV template data for songs
  const csvTemplate = [
    {
      title: 'Example Song',
      musical_title: 'Example Musical',
      act_number: 1,
      scene_number: 1,
      song_order: 1,
      tempo: 'Moderato',
      key_signature: 'C Major',
      time_signature: '4/4',
      duration_seconds: 180,
      is_featured: 'false',
      lyrics: 'Example lyrics content...'
    }
  ];

  const requiredColumns = ['title', 'musical_title'];

  // Memoized filtered and sorted songs
  const filteredAndSortedSongs = useMemo(() => {
    let filtered = [...songs];

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (selectedSort) {
        case 'recently-updated':
          return new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0);
        case 'title-asc':
          return (a?.title || '')?.localeCompare(b?.title || '');
        case 'musical-asc':
          return (a?.musicalTitle || '')?.localeCompare(b?.musicalTitle || '');
        case 'newest':
        default:
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });

    return filtered;
  }, [songs, selectedSort]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!loading && songs?.length === 0) {
    return <EmptyState onAddNewSong={handleAddNewSong} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Song Database - RoleCall</title>
        <meta name="description" content="Browse and search through comprehensive musical theater song analyses with advanced fuzzy search functionality." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-white mb-2">
              Song Database
            </h1>
            <p className="text-blue-100 font-body">
              Search through comprehensive song analyses or view all songs - press Enter to search!
            </p>
          </div>

          {/* Enhanced Search Bar */}
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            selectedGenre={selectedGenre}
            onGenreChange={handleGenreFilter}
            selectedSort={selectedSort}
            onSortChange={handleSort}
            genreOptions={Object.keys(stats?.by_genre || {})}
            totalSongs={songs?.length || 0}
          />

          {/* Filter Stats with search context - only show if songs are being displayed */}
          {showSongs && !searchTerm && (
            <FilterStats
              totalCount={songs?.length || 0}
              filteredCount={filteredSongs?.length || 0}
              searchTerm={searchTerm}
              selectedGenre={selectedGenre}
              stats={stats}
            />
          )}

          {/* Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center space-x-4 flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconPosition="left"
                disabled={loading}
                className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              
              {!showSongs && (
                <Button
                  variant="outline"
                  onClick={handleViewAllSongs}
                  iconName="Database"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                >
                  View All Songs
                </Button>
              )}

              {/* View Mode Toggle - only show when songs are displayed */}
              {showSongs && (
                <Button
                  variant="outline"
                  onClick={handleToggleViewMode}
                  iconName={viewMode === 'full' ? 'List' : 'Grid'}
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                >
                  {viewMode === 'full' ? 'Compact View' : 'Full View'}
                </Button>
              )}

              {/* Filter Buttons - only show when songs are displayed */}
              {showSongs && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleToggleNoRolesFilter}
                    iconName="UserX"
                    iconPosition="left"
                    className={`rounded-lg transition-all ${
                      filterNoRoles
                        ? 'bg-blue-500 bg-opacity-30 border-blue-300 text-white' :'border-white text-white hover:bg-white hover:text-indigo-900'
                    }`}
                  >
                    No Roles
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleToggleNoAnalysisFilter}
                    iconName="FileX"
                    iconPosition="left"
                    className={`rounded-lg transition-all ${
                      filterNoAnalysis
                        ? 'bg-purple-500 bg-opacity-30 border-purple-300 text-white' :'border-white text-white hover:bg-white hover:text-indigo-900'
                    }`}
                  >
                    No Analysis
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleToggleNoMusicFilter}
                    iconName="Music"
                    iconPosition="left"
                    className={`rounded-lg transition-all ${
                      filterNoMusic
                        ? 'bg-orange-500 bg-opacity-30 border-orange-300 text-white' :'border-white text-white hover:bg-white hover:text-indigo-900'
                    }`}
                  >
                    No Music
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleToggleNoVideosFilter}
                    iconName="Video"
                    iconPosition="left"
                    className={`rounded-lg transition-all ${
                      filterNoVideos
                        ? 'bg-pink-500 bg-opacity-30 border-pink-300 text-white' :'border-white text-white hover:bg-white hover:text-indigo-900'
                    }`}
                  >
                    No Videos
                  </Button>
                </>
              )}
              
              {/* Only show buttons to SuperUsers */}
              {isSuperUser() && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowCSVImport(true)}
                    iconName="Upload"
                    iconPosition="left"
                    className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                  >
                    Import CSV
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setShowSongFileImport(true)}
                    iconName="FileText"
                    iconPosition="left"
                    className="border-green-400 text-green-400 hover:bg-green-400 hover:text-indigo-900 rounded-lg"
                  >
                    Upload Song File
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setShowDuplicateCleanup(true)}
                    iconName="Copy"
                    iconPosition="left"
                    className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-indigo-900 rounded-lg"
                  >
                    Remove Duplicates
                  </Button>
                </>
              )}
              
              {/* Only show sort controls when songs are being displayed */}
              {showSongs && (
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-blue-100">Sort by:</label>
                  <Select
                    value={selectedSort}
                    onChange={(e) => handleSort(e?.target?.value)}
                    options={[
                      { value: 'recently-updated', label: 'Most Recently Updated' },
                      { value: 'newest', label: 'Newest' },
                      { value: 'oldest', label: 'Oldest' },
                      { value: 'title-asc', label: 'Song Name (A-Z)' },
                      { value: 'title-desc', label: 'Song Name (Z-A)' },
                      { value: 'musical-asc', label: 'Musical (A-Z)' },
                      { value: 'musical-desc', label: 'Musical (Z-A)' },
                      { value: 'year-asc', label: 'Year (Oldest First)' },
                      { value: 'year-desc', label: 'Year (Newest First)' }
                    ]}
                    className="w-40 bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-lg"
                  />
                </div>
              )}
            </div>
            
            <Button
              variant="default"
              onClick={handleAddNewSong}
              iconName="Plus"
              iconPosition="left"
              className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
            >
              Add New Song
            </Button>
          </div>

          {/* Enhanced Loading indicator for fuzzy search */}
          {searchLoading && (
            <div className="bg-blue-50 bg-opacity-90 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="Search" size={20} className="text-blue-600 animate-pulse" />
                <p className="text-blue-800 font-medium">
                  Searching with advanced fuzzy matching for better results...
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-4 border-red-400 text-red-600 hover:bg-red-400 hover:text-white rounded-lg"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Welcome message when songs are not being displayed */}
          {!showSongs && !loading && (
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Search" size={32} className="text-blue-100" />
              </div>

              <h2 className="text-xl font-heading font-semibold text-white mb-3">
                Ready to explore the song database?
              </h2>

              <p className="text-blue-100 font-body mb-8 max-w-md mx-auto leading-relaxed">
                Use the search bar above to find specific songs, or click "View All Songs" to browse the entire collection.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="default"
                  onClick={handleViewAllSongs}
                  iconName="Database"
                  iconPosition="left"
                  className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
                >
                  View All Songs
                </Button>
                <Button
                  variant="outline"
                  onClick={handleAddNewSong}
                  iconName="Plus"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                >
                  Add New Song
                </Button>
              </div>
            </div>
          )}

          {/* Enhanced no results message - only show when songs are being displayed */}
          {showSongs && filteredSongs?.length === 0 && searchTerm && !searchLoading && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto text-blue-300 mb-4" />
              <p className="text-white text-lg mb-2">
                No songs found for "{searchTerm}"
                {selectedGenre !== 'all' && ` in ${selectedGenre} genre`}
              </p>
              <p className="text-blue-200 text-sm">
                Try different keywords or check for typos. Our fuzzy search handles partial matches!
              </p>
            </div>
          )}

          {/* Songs List - conditional rendering based on view mode */}
          {showSongs && filteredSongs?.length > 0 && (
            <div className={viewMode === 'full' ? 'space-y-4' : 'space-y-2'}>
              {filteredSongs?.map((song) => (
                <SongListItem 
                  key={song?.id} 
                  song={song}
                  onUpdate={loadSongs}
                  onClick={handleSongClick}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorited={favorites?.has(song?.id)}
                  showFavoriteButton={!!user}
                  isFeatured={featuredSongIds?.has(song?.id)}
                  onFeatureToggle={isSuperUser() ? handleFeatureToggle : null}
                  viewMode={viewMode}
                />
              ))}
            </div>
          )}

          {/* Enhanced Footer Stats - only show when songs are being displayed */}
          {showSongs && filteredSongs?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white border-opacity-20">
              <div className="flex items-center justify-between text-sm text-blue-100">
                <div className="flex items-center space-x-2">
                  <Icon name="Database" size={16} />
                  <span className="font-caption">
                    {searchTerm ? `Found ${filteredSongs?.length} of ${songs?.length}` : `Showing ${filteredSongs?.length}`} songs
                    {searchTerm && ' with advanced fuzzy search'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span className="font-caption">
                    Last updated: {new Date()?.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Modals - Only render if SuperUser */}
        {isSuperUser() && (
          <>
            <CSVImportModal
              isOpen={showCSVImport}
              onClose={() => setShowCSVImport(false)}
              title="Import Songs from CSV"
              templateData={csvTemplate}
              requiredColumns={requiredColumns}
              onImport={handleCSVImport}
              processingText="Importing songs..."
              successText="songs imported successfully!"
            />

            <DuplicateSongModal
              isOpen={showDuplicateModal}
              onClose={() => setShowDuplicateModal(false)}
              duplicates={duplicateData?.duplicates}
              originalData={duplicateData?.originalData}
              onMerge={handleMergeDuplicates}
              onCreateNew={handleCreateNewSong}
              title="Duplicate Songs Found"
            />

            <DuplicateSongCleanupModal
              isOpen={showDuplicateCleanup}
              onClose={() => setShowDuplicateCleanup(false)}
              onCleanup={handleDuplicateCleanup}
              title="Remove Duplicate Songs"
            />

            <SongFileImportModal
              isOpen={showSongFileImport}
              onClose={() => setShowSongFileImport(false)}
              onImport={handleSongFileImport}
              existingSongs={songs}
              onDuplicatesFound={handleDuplicatesFound}
            />

            <SongDuplicateResolutionModal
              isOpen={showDuplicateResolution}
              onClose={() => setShowDuplicateResolution(false)}
              duplicateData={duplicateResolutionData}
              onMerge={handleMergeDuplicateSongs}
              onCreateNew={handleCreateNewSongDespiteDuplicates}
              onSkip={() => setShowDuplicateResolution(false)}
            />
          </>
        )}
      </div>
    </div>
  );
}